import { usePriceBooks } from "@/hooks/usePriceBooks";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BookOpen, Calendar } from "lucide-react";
import { format } from "date-fns";

export function PriceBookSettings() {
  const { priceBooks, isLoading } = usePriceBooks();

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Price Books</CardTitle>
          <CardDescription>
            Manage county-specific price books and their effective dates
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Price Book Name</TableHead>
                  <TableHead>County</TableHead>
                  <TableHead>Effective Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center">
                      Loading price books...
                    </TableCell>
                  </TableRow>
                ) : priceBooks?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center">
                      No price books found
                    </TableCell>
                  </TableRow>
                ) : (
                  priceBooks?.map((priceBook) => (
                    <TableRow key={priceBook.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <BookOpen className="h-4 w-4 text-muted-foreground" />
                          {priceBook.name}
                        </div>
                      </TableCell>
                      <TableCell>{priceBook.county}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          {format(new Date(priceBook.effective_date), "MMM d, yyyy")}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={priceBook.is_active ? "default" : "secondary"}>
                          {priceBook.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm">
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Import / Export</CardTitle>
          <CardDescription>
            Bulk manage products and prices using CSV files
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <h4 className="font-medium">Import Products</h4>
              <p className="text-sm text-muted-foreground">
                Upload a CSV file to add or update products in bulk
              </p>
              <Button variant="outline" disabled>
                Choose CSV File
              </Button>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium">Export Price Book</h4>
              <p className="text-sm text-muted-foreground">
                Download current products and prices as CSV
              </p>
              <Button variant="outline" disabled>
                Download CSV
              </Button>
            </div>
          </div>
          <div className="mt-4 p-4 bg-muted/50 rounded-lg">
            <p className="text-sm text-muted-foreground">
              <strong>Note:</strong> Import/Export functionality coming soon. This will allow you to manage large catalogs efficiently.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
